import { DateTime } from 'luxon';

import type { MaskHole } from './radialHoleMask';
import { ContactAvatar, TeamUserAvatar } from './Avatar';
import { cn } from './cn';
import { usePlatformImage } from './contexts';
import { Button } from './Input2';
import { radialHoleMask } from './radialHoleMask';
import { SignalsTooltipLabel } from './SignalsTooltipLabel';
import { Communication } from './StrokeIcons';

type ContactCardImageWithOwnerProps = {
  name: string;

  firstName?: string | null;
  lastName?: string | null;
  imageSrc?: string | null;

  /**
   * Trimmed cutout URL (background-removed, transparent edges cropped). When
   * `showCutout` is true and this is set, the card renders a "standing cutout"
   * (transparent, natural aspect, stands on the floor and bleeds above the
   * frame — like the Oracle people list) instead of the circular avatar.
   */
  cutoutSrc?: string | null;
  showCutout?: boolean;

  orgPictureUrl?: string | null;

  cadenceName?: string | null;

  showContactUser?: boolean;
  contactUserProfilePicture?: string | null;
  contactUserLiConnected?: boolean | null;
  contactUserLiConnectedAt?: Date | null;
  contactUserName?: string | null;
  contactUserEmail?: string | null;

  authedUserLiConnected?: boolean | null;
  authedUserLiConnectedAt?: Date | null;

  signalsComputed?: boolean;
  triggeredSignals?: { bucketName: string; reason: string | null; isIgnored?: boolean }[] | null;

  size?: 'default' | 'pipeline';
  isProcessing?: boolean;
};

export function ContactCardImageWithOwner({
  name,
  firstName,
  lastName,
  imageSrc,
  cutoutSrc,
  showCutout,
  orgPictureUrl,
  cadenceName,
  showContactUser,
  contactUserProfilePicture,
  contactUserLiConnected,
  contactUserLiConnectedAt,
  contactUserName,
  contactUserEmail,
  authedUserLiConnected,
  authedUserLiConnectedAt,
  signalsComputed,
  triggeredSignals,
  size = 'default',
  isProcessing,
}: ContactCardImageWithOwnerProps) {
  const avatarSizeClass = size === 'pipeline' ? 'size-11.5' : 'size-17.5';
  const authedConnected = authedUserLiConnected ?? (authedUserLiConnectedAt != null ? true : null);
  // Without the owner badge, the dot also covers a teammate's connection (the
  // pipeline sorts on it), so a connected contact never looks unconnected.
  const ownerConnected = contactUserLiConnected ?? (contactUserLiConnectedAt != null ? true : null);
  const showAuthedDot = !showContactUser && (authedConnected === true || ownerConnected === true);
  const showSignalIcon = signalsComputed === true;
  const activeSignals = triggeredSignals?.filter((s) => !s.isIgnored) ?? [];
  const signalsTriggered = activeSignals.length > 0;
  const allSignalsIgnored = (triggeredSignals?.length ?? 0) > 0 && activeSignals.length === 0;
  // Cut transparent circles out of the contact image where badges sit so the
  // card background shows through as a 2px ring. Owner badge (bottom-left):
  // avatar 22/16px at offset -6/-4px, hole radius = avatar radius + 2. Authed
  // dot (bottom-left corner): 10/6px, hole radius = dot radius + 2. Signal
  // icon (top-right): same radius as owner badge.
  const holes: MaskHole[] = [];
  if (showContactUser) {
    holes.push(
      size === 'pipeline' ? { at: '6px calc(100% - 6px)', radius: 10 } : { at: '7px calc(100% - 7px)', radius: 13 },
    );
  } else if (showAuthedDot) {
    holes.push(size === 'pipeline' ? { at: '0 100%', radius: 5 } : { at: '0 100%', radius: 7 });
  }
  if (showSignalIcon) {
    holes.push({ at: 'calc(100% - 5px) 5px', radius: 12 });
  }
  const maskStyle = radialHoleMask(holes);

  // Show the standing cutout only when the team flag is on, a trimmed cutout
  // exists, and the photo isn't mid-processing (fall back to the avatar/spinner).
  const cutoutToShow = showCutout === true && isProcessing !== true ? cutoutSrc : null;

  return (
    <div className={cn('relative shrink-0', avatarSizeClass)}>
      {cutoutToShow != null && <ContactCardCutout src={cutoutToShow} alt={name} />}
      {cutoutToShow == null && (
        <ContactAvatar
          firstName={firstName}
          lastName={lastName}
          imageSrc={imageSrc}
          orgImageSrc={orgPictureUrl}
          isProcessing={isProcessing}
          shape="square"
          className="size-full"
          style={maskStyle}
        />
      )}
      {showContactUser && (
        <div className={cn('absolute', size === 'pipeline' ? '-bottom-0.5 -left-0.5' : '-bottom-1 -left-1')}>
          <TeamUserAvatar
            // The ring and the tooltip describe the same person: the owner.
            connected={ownerConnected ?? undefined}
            userTeam={{
              profilePicture: contactUserProfilePicture ?? null,
              userEmail: contactUserEmail ?? null,
              userTeamName: contactUserName ?? null,
            }}
            className={cn({ 'size-4': size === 'pipeline', 'size-5.5': size === 'default' })}
            shape="circle"
            tooltip={ownerBadgeTooltip({
              contactName: name,
              cadenceName,
              ownerName: contactUserName ?? contactUserEmail,
              ownerConnected,
              ownerConnectedAt: contactUserLiConnectedAt,
              authedConnected,
              authedConnectedAt: authedUserLiConnectedAt,
            })}
          />
        </div>
      )}

      {showAuthedDot && (
        <div
          className={cn(
            'absolute bottom-0 left-0 -translate-x-1/2 translate-y-1/2 rounded-full',
            size === 'pipeline' ? 'size-1.5' : 'size-2.5',
            'bg-mantis-500',
          )}
          data-testid="connection-dot"
          title={connectionDotTooltip({
            name,
            ownerName: contactUserName,
            ownerConnected,
            ownerConnectedAt: contactUserLiConnectedAt,
            authedConnected,
            authedConnectedAt: authedUserLiConnectedAt,
          })}
        />
      )}

      {showSignalIcon && (
        <div className={cn('absolute top-[5px] right-[5px] -translate-y-1/2 translate-x-1/2')}>
          <Button
            size="2xs"
            color={signalsTriggered ? 'green' : allSignalsIgnored ? 'green' : 'gray'}
            tone={allSignalsIgnored && !signalsTriggered ? 'soft' : 'filled'}
            text={!signalsTriggered && !allSignalsIgnored}
            readOnly
            icon={<Communication.Rss01 />}
            shape="pill"
            className={cn(allSignalsIgnored && 'border border-dashed border-mantis-500/60 opacity-70')}
            tooltipMaxWidth={320}
            tooltip={
              (triggeredSignals?.length ?? 0) > 0 ? (
                <SignalsTooltipLabel signals={triggeredSignals ?? []} />
              ) : (
                'Signals computed — none triggered'
              )
            }
          />
        </div>
      )}
    </div>
  );
}

/**
 * The "standing cutout" treatment (like the Oracle people list): a transparent,
 * background-removed contact photo shown at its natural aspect, bottom-aligned so
 * it stands on the image slot's floor and bleeds above the top edge. Rendered in
 * place of the circular avatar when the team's cutout flag is on and a trimmed
 * variant exists. Uses the platform image (next/image on the dashboard) so it
 * still gets optimization — it just isn't clipped to a square like the avatar.
 */
function ContactCardCutout({ src, alt }: { src: string; alt: string }) {
  const Image = usePlatformImage();
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[150%]" data-testid="contact-card-cutout">
      <Image alt={alt} src={src} fill className="object-contain object-bottom" />
    </div>
  );
}

const fmtDate = (d: Date) => DateTime.fromJSDate(d).toLocaleString(DateTime.DATE_FULL);

/**
 * One person's LinkedIn connection to the contact, as a standing fact ("have been connected
 * since"), never as an action: the connection may have come from anywhere (a manual request,
 * another campaign, the contact asking first), so the line must not read as something a
 * campaign did.
 */
function connectionLine(p: { who: string; name: string; connectedAt: Date | null | undefined }): string {
  if (p.connectedAt != null) return `${p.who} and ${p.name} have been connected since ${fmtDate(p.connectedAt)}`;
  return `${p.who} ${p.who === 'You' ? 'are' : 'is'} connected to ${p.name}`;
}

/** connectionLine, plus the not-connected and unknown states. */
function connectionStatusLine(p: {
  who: string;
  name: string;
  connected: boolean | null;
  connectedAt: Date | null | undefined;
}): string {
  if (p.connected === true) return connectionLine(p);
  if (p.connected === false) return `${p.who} ${p.who === 'You' ? 'are' : 'is'} not connected to ${p.name}`;
  return `${p.who === 'You' ? 'Your' : `${p.who}'s`} connection to ${p.name} is unknown`;
}

/** Hover text for the connection dot: the campaign owner's connection, then the viewer's. */
function connectionDotTooltip(p: {
  name: string;
  ownerName: string | null | undefined;
  ownerConnected: boolean | null;
  ownerConnectedAt: Date | null | undefined;
  authedConnected: boolean | null;
  authedConnectedAt: Date | null | undefined;
}): string {
  const lines: string[] = [];
  if (p.ownerConnected === true) {
    lines.push(connectionLine({ who: p.ownerName ?? 'A teammate', name: p.name, connectedAt: p.ownerConnectedAt }));
  }
  if (p.authedConnected === true) {
    lines.push(connectionLine({ who: 'You', name: p.name, connectedAt: p.authedConnectedAt }));
  }
  return lines.join('\n');
}

/**
 * Hover text for the owner badge: who runs the campaign, then (each on its own line) where
 * that person stands with the contact on LinkedIn, and where the viewer stands when that is
 * known and differs. Being connected says nothing about whether this campaign made the
 * connection, so the campaign and the connection never share a sentence. The badge is the
 * owner's, so the owner's line comes first and matches the badge's green ring; the viewer's
 * line is dropped when it would repeat the owner's (the usual case: the viewer owns the
 * campaign, and the component has no ids to tell the two apart).
 */
function ownerBadgeTooltip(p: {
  contactName: string;
  cadenceName: string | null | undefined;
  ownerName: string | null | undefined;
  ownerConnected: boolean | null;
  ownerConnectedAt: Date | null | undefined;
  authedConnected: boolean | null;
  authedConnectedAt: Date | null | undefined;
}): string {
  const owner = p.ownerName ?? 'A teammate';
  const lines = [
    p.cadenceName ? `${owner} is running campaign ${p.cadenceName}` : owner,
    connectionStatusLine({
      who: owner,
      name: p.contactName,
      connected: p.ownerConnected,
      connectedAt: p.ownerConnectedAt,
    }),
  ];
  const sameAsOwner =
    p.authedConnected === p.ownerConnected && p.authedConnectedAt?.getTime() === p.ownerConnectedAt?.getTime();
  if (p.authedConnected != null && !sameAsOwner) {
    lines.push(
      connectionStatusLine({
        who: 'You',
        name: p.contactName,
        connected: p.authedConnected,
        connectedAt: p.authedConnectedAt,
      }),
    );
  }
  return lines.join('\n');
}
